import { apiError } from "../../../../../../utils/formErrors";
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Stack, Chip, CircularProgress, IconButton, MenuItem, TextField, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import EmptyState from "../../../../../../component/EmptyState";
import { useNotify } from "../../../../../../component/NotificationProvider";
import {
  getOnboardingTemplates,
  createOnboardingTemplate,
  updateOnboardingTemplate,
} from "../../../../../../services/onboarding";
import { tokens, radius } from "../../../../../../theme";
import {
  SettingsField,
  SettingsSaveBar,
  SettingsSection,
} from "../../../../../../component/settings";

import { pressable } from "../../../../../../component/pressableProps";
/**
 * What onboarding means at this organisation.
 *
 * The editor an admin actually needs is a list they can reorder, not a form
 * builder — the variation between customers is which documents get collected
 * and which tasks happen, so those are the only two kinds of row.
 *
 * Every field on a row answers a question somebody asks during onboarding:
 * who owes this, does it block, and by when relative to the joining date.
 * "Due" is an offset rather than a date because a template outlives any
 * particular hire — and it can be negative, which is the case that matters:
 * a signed offer letter is due before somebody starts.
 */

const cardSx = {
  background: tokens.card,
  border: `1px solid ${tokens.border}`,
  borderRadius: `${radius.card}px`,
  padding: "1.25rem",
};

const OWNERS = [
  { value: "employee", label: "The employee" },
  { value: "admin", label: "HR / admin" },
  { value: "it", label: "IT" },
  { value: "manager", label: "Their manager" },
];

const blankItem = () => ({
  kind: "task",
  label: "",
  description: "",
  owner: "admin",
  required: true,
  dueOffsetDays: 0,
});

const dueLabel = (days) => {
  const n = Number(days) || 0;
  if (n === 0) return "on the joining date";
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? "" : "s"} before joining`;
  return `${n} day${n === 1 ? "" : "s"} after joining`;
};

const OnboardingTemplates = ({ phase = "onboarding" }) => {
  const notify = useNotify();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOnboardingTemplates(phase);
      const list = Array.isArray(res?.data) ? res.data : [];
      setTemplates(list);
      setSelected((current) => current || list[0]?._id || null);
    } catch (err) {
      notify.error(err?.message || "Couldn't load onboarding templates");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const found = templates.find((t) => t._id === selected);
    setDraft(found ? JSON.parse(JSON.stringify(found)) : null);
  }, [selected, templates]);

  /**
   * What is unsaved, measured against the template the draft was cloned from.
   *
   * Items are compared on the fields that actually get sent — a server-side
   * `_id` or a re-derived `order` would otherwise make every draft look dirty
   * the moment it loaded, and a save bar that is always up is the same as no
   * save bar at all.
   */
  const baseline = templates.find((t) => t._id === draft?._id) || null;
  const dirtyCount = React.useMemo(() => {
    if (!draft) return 0;
    if (!draft._id || !baseline) return 1; // a template not yet created
    const steps = (t) =>
      JSON.stringify(
        (t.items || []).map((i) => ({
          kind: i.kind, label: i.label || "", description: i.description || "",
          owner: i.owner, required: Boolean(i.required), dueOffsetDays: Number(i.dueOffsetDays) || 0,
        }))
      );
    let n = 0;
    if ((draft.name || "") !== (baseline.name || "")) n += 1;
    if (Boolean(draft.isDefault) !== Boolean(baseline.isDefault)) n += 1;
    if ((draft.appliesTo?.onboardingType || "") !== (baseline.appliesTo?.onboardingType || "")) n += 1;
    if (steps(draft) !== steps(baseline)) n += 1;
    return n;
  }, [draft, baseline]);

  const discard = () =>
    setDraft(baseline ? JSON.parse(JSON.stringify(baseline)) : null);

  const setItem = (index, patch) =>
    setDraft((d) => ({
      ...d,
      items: d.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  const move = (index, by) =>
    setDraft((d) => {
      const items = [...d.items];
      const target = index + by;
      if (target < 0 || target >= items.length) return d;
      [items[index], items[target]] = [items[target], items[index]];
      return { ...d, items: items.map((item, i) => ({ ...item, order: i })) };
    });

  const save = async () => {
    if (!draft?.name?.trim()) return notify.error("The template needs a name");
    const unlabelled = draft.items.findIndex((i) => !i.label?.trim());
    if (unlabelled >= 0) return notify.error(`Step ${unlabelled + 1} needs a label`);

    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        description: draft.description,
        isDefault: draft.isDefault,
        appliesTo: draft.appliesTo,
        items: draft.items.map((item, order) => ({ ...item, order })),
        // Stamped on save, or an exit template created from this screen would
        // be stored as a joining one and start being handed to new hires.
        phase,
      };
      const res = draft._id
        ? await updateOnboardingTemplate(draft._id, payload)
        : await createOnboardingTemplate(payload);
      if (res?.success === false) throw apiError(res);
      // Said out loud: an admin editing a template reasonably wonders whether
      // it reaches people already part-way through. It does not.
      notify.success("Saved — checklists already assigned keep the steps they were given");
      setSelected(res?.data?._id || draft._id);
      await load();
    } catch (err) {
      notify.error(err?.message || "Couldn't save the template");
    } finally {
      setSaving(false);
    }
  };

  const startNew = () =>
    setDraft({
      name: "",
      description: "",
      isDefault: false,
      appliesTo: {},
      items: [blankItem()],
    });

  if (loading) {
    return (
      <Box sx={{ padding: "2rem", textAlign: "center" }}>
        <CircularProgress size={24} sx={{ color: tokens.textMuted }} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "1rem", flexWrap: "wrap", rowGap: "0.75rem" }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: "0.5rem" }}>
          {templates.map((t) => (
            <Chip
              key={t._id}
              label={t.isDefault ? `${t.name} · default` : t.name}
              onClick={() => setSelected(t._id)}
              variant={selected === t._id && draft?._id ? "filled" : "outlined"}
              sx={{
                background: selected === t._id && draft?._id ? `${tokens.accentTint}` : "transparent",
                color: selected === t._id && draft?._id ? tokens.accent : tokens.text,
                borderColor: tokens.border,
              }}
            />
          ))}
        </Stack>
        <BtnPrimary onClick={startNew} sx={{ paddingInline: "1rem" }}>
          New template
        </BtnPrimary>
      </Stack>

      {!draft ? (
        <EmptyState
          title="No onboarding template"
          description="A template says what onboarding means here — the documents you collect and the tasks that happen. New hires get a checklist from it automatically."
        />
      ) : (
        <Box sx={cardSx}>
          {/* One column, not the three-across row this was. The book rules out
              a multi-column settings form by name, and "Applies to" beside
              "Default" is exactly the pair that gets set the wrong way round. */}
          <SettingsSection>
            <SettingsField label="Template name">
              <TextField
                size="small" fullWidth value={draft.name || ""}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </SettingsField>
            <SettingsField label="Applies to" help="Leave blank to use for every hire.">
              <TextField
                select size="small" fullWidth
                value={draft.appliesTo?.onboardingType || ""}
                onChange={(e) =>
                  setDraft({ ...draft, appliesTo: { ...draft.appliesTo, onboardingType: e.target.value || undefined } })
                }
              >
                <MenuItem value="">Every hire</MenuItem>
                <MenuItem value="Remote">Remote joiners</MenuItem>
                <MenuItem value="Office">Office joiners</MenuItem>
              </TextField>
            </SettingsField>
            <SettingsField label="Default" help="Used when nothing more specific fits.">
              <TextField
                select size="small" fullWidth
                value={draft.isDefault ? "yes" : "no"}
                onChange={(e) => setDraft({ ...draft, isDefault: e.target.value === "yes" })}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </SettingsField>
          </SettingsSection>

          {draft.items.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: "10px",
                padding: "1rem",
                mb: "0.75rem",
              }}
            >
              <Stack direction="row" spacing={1} sx={{ mb: "0.75rem", flexWrap: "wrap", rowGap: "0.75rem" }}>
                <TextField
                  label="Step" size="small" sx={{ flex: 2, minWidth: "12rem" }}
                  value={item.label || ""}
                  onChange={(e) => setItem(index, { label: e.target.value })}
                />
                <TextField
                  select label="Kind" size="small" sx={{ width: "8rem" }}
                  value={item.kind}
                  onChange={(e) => setItem(index, { kind: e.target.value })}
                >
                  <MenuItem value="task">Task</MenuItem>
                  <MenuItem value="document">Document</MenuItem>
                </TextField>
                <TextField
                  select label="Who does it" size="small" sx={{ width: "10rem" }}
                  value={item.owner}
                  onChange={(e) => setItem(index, { owner: e.target.value })}
                >
                  {OWNERS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField
                  label="Due (days)" size="small" type="number" sx={{ width: "8rem" }}
                  value={item.dueOffsetDays ?? 0}
                  onChange={(e) => setItem(index, { dueOffsetDays: Number(e.target.value) })}
                  helperText={dueLabel(item.dueOffsetDays)}
                />
                <TextField
                  select label="Required" size="small" sx={{ width: "8rem" }}
                  value={item.required === false ? "no" : "yes"}
                  onChange={(e) => setItem(index, { required: e.target.value === "yes" })}
                  helperText={item.required === false ? "does not block" : "blocks completion"}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Note for whoever does it (optional)" size="small" fullWidth
                  value={item.description || ""}
                  onChange={(e) => setItem(index, { description: e.target.value })}
                />
                <Tooltip title="Move up"><span>
                  <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Move down"><span>
                  <IconButton size="small" disabled={index === draft.items.length - 1} onClick={() => move(index, 1)}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
                <Tooltip title="Remove step"><span>
                  <IconButton
                    size="small"
                    onClick={() => setDraft({ ...draft, items: draft.items.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span></Tooltip>
              </Stack>
            </Box>
          ))}

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: "1rem" }}>
            <Box
              {...pressable(() => setDraft({ ...draft, items: [...draft.items, blankItem()] }))}
              sx={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", color: tokens.accent, fontWeight: 600, fontSize: "0.875rem" }}
            >
              <AddIcon fontSize="small" /> Add a step
            </Box>
          </Stack>

          <SettingsSaveBar
            dirtyCount={dirtyCount}
            onDiscard={draft._id ? discard : undefined}
            onSave={save}
            saving={saving}
            label={draft._id ? "Save template" : "Create template"}
            message={draft._id ? undefined : "This template has not been created yet"}
          />
        </Box>
      )}
    </Box>
  );
};

export default OnboardingTemplates;
